import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { AgencyLogo } from "@/components/Navbar";
import { useSEO } from "@/hooks/useSEO";

const inputCls =
  "w-full border border-white/15 bg-black/40 px-4 py-3.5 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-gold";

export default function AdminLogin() {
  useSEO("Login — Diamotims Agency");
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "Sign in failed."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="admin-login-page" className="flex min-h-screen items-center justify-center bg-obsidian px-4">
      <div className="w-full max-w-md border border-white/10 bg-[#121212] p-8 sm:p-10">
        <div className="flex items-center gap-4">
          <AgencyLogo className="h-16 w-auto" />
          <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-gold">CMS</div>
        </div>
        <form data-testid="admin-login-form" onSubmit={submit} className="mt-8 space-y-5">
          <label className="block">
            <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Email</span>
            <input data-testid="admin-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} autoComplete="username" />
          </label>
          <label className="block">
            <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Password</span>
            <input data-testid="admin-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} autoComplete="current-password" />
          </label>
          {error && (
            <div data-testid="admin-login-error" className="border border-red-500/40 bg-red-500/10 px-4 py-3 text-xs text-red-300">
              {error}
            </div>
          )}
          <button data-testid="admin-login-submit" type="submit" disabled={busy} className="group flex w-full items-center justify-center gap-3 bg-ivory px-8 py-4 font-mono text-xs uppercase tracking-[0.3em] text-black transition-colors duration-300 hover:bg-gold disabled:opacity-50">
            {busy ? "Signing in…" : "Sign In"}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
          </button>
        </form>
      </div>
    </main>
  );
}
